// @ts-check
// Formatação de números, moedas, datas e horas com as preferências do membro (Configurações > Experiência): fuso,
// ordem da data, 12 ou 24 horas e separadores numéricos. Sem preferência, vale o padrão de Brasília.
import { LOCALE } from '../i18n/index.js';
import { formatPreferencesState, numberLocaleOf } from './format-preferences.js';

const MICROS_PER_UNIT = 1_000_000;

/** @type {Map<string, Intl.NumberFormat>} */
const numberFormatters = new Map();
/** @type {Map<string, Intl.DateTimeFormat>} */
const dateTimeFormatters = new Map();

/** @param {string} locale @param {Intl.NumberFormatOptions} options */
function cachedNumberFormat(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat(locale, options);
    } catch {
      // moeda desconhecida: número simples
      formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    }
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

/** @param {string} locale @param {Intl.DateTimeFormatOptions} options */
function cachedDateTimeFormat(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateTimeFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatters.set(key, formatter);
  }
  return formatter;
}

/** Preferências atuais (signal: quem formata durante a renderização acompanha a troca). */
export const getFormatPreferences = () => formatPreferencesState.value;

/** Localidade dos números conforme a preferência (1.234,56 ou 1,234.56). */
export const getNumberLocale = () => numberLocaleOf(formatPreferencesState.value);

/**
 * @param {number | string | null | undefined} value
 * @param {{ decimals?: number, percentage?: boolean }} [options]
 */
export function formatNumber(value, { decimals, percentage = false } = {}) {
  if (value === null || value === undefined || value === '') return '';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);
  const formatter = cachedNumberFormat(getNumberLocale(), {
    style: percentage ? 'percent' : 'decimal',
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 2,
  });
  return formatter.format(percentage ? numericValue / 100 : numericValue);
}

/** Número compacto (1,2 mil) nos separadores da preferência. @param {number} value */
export function formatCompactNumber(value) {
  return cachedNumberFormat(getNumberLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * @param {number | string | null | undefined} amountMicros
 * @param {string | null | undefined} [currencyCode]
 * @param {{ short?: boolean, decimals?: number }} [options] decimals: casas fixas no formato completo (settings do campo)
 */
export function formatCurrencyMicros(amountMicros, currencyCode = 'BRL', { short = false, decimals } = {}) {
  if (amountMicros === null || amountMicros === undefined || amountMicros === '') return '';
  const amount = Number(amountMicros) / MICROS_PER_UNIT;
  return formatCurrency(amount, currencyCode ?? 'BRL', { short, decimals });
}

/**
 * @param {number} amount
 * @param {string} currencyCode
 * @param {{ short?: boolean, decimals?: number }} [options]
 */
export function formatCurrency(amount, currencyCode = 'BRL', { short = false, decimals } = {}) {
  const fixedDecimals = !short && typeof decimals === 'number' ? decimals : null;
  return cachedNumberFormat(getNumberLocale(), {
    style: 'currency',
    currency: currencyCode || 'BRL',
    notation: short ? 'compact' : 'standard',
    ...(fixedDecimals === null
      ? { maximumFractionDigits: short ? 1 : 2 }
      : { minimumFractionDigits: fixedDecimals, maximumFractionDigits: fixedDecimals }),
  }).format(amount);
}

/**
 * @param {number | string} amount
 */
export function toAmountMicros(amount) {
  return Math.round(Number(amount) * MICROS_PER_UNIT);
}

/**
 * @param {number | string | null | undefined} amountMicros
 */
export function fromAmountMicros(amountMicros) {
  if (amountMicros === null || amountMicros === undefined || amountMicros === '') return '';
  return String(Number(amountMicros) / MICROS_PER_UNIT);
}

/**
 * Dia, mês e ano de um instante no fuso pedido, com dois dígitos.
 * @param {Date} date @param {string} timeZone
 */
function calendarPartsOf(date, timeZone) {
  /** @type {Record<string, string>} */
  const parts = {};
  for (const part of cachedDateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).formatToParts(date)) {
    parts[part.type] = part.value;
  }
  return { year: parts.year, month: parts.month, day: parts.day };
}

/**
 * DD/MM/AAAA ou AAAA-MM-DD, conforme a preferência.
 * @param {{ year: string, month: string, day: string }} parts
 */
function joinDateParts({ year, month, day }) {
  return getFormatPreferences().dateFormat === 'YEAR_FIRST' ? `${year}-${month}-${day}` : `${day}/${month}/${year}`;
}

/**
 * Hora (14:30 ou 2:30 PM). Por padrão no fuso da preferência; a agenda passa o do aparelho, porque a grade de horas
 * é montada nele.
 * @param {string | Date | null | undefined} value
 * @param {{ timeZone?: string }} [options]
 */
export function formatTime(value, { timeZone } = {}) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const isTwelveHours = getFormatPreferences().timeFormat === 'HOUR_12';
  return cachedDateTimeFormat(isTwelveHours ? 'en-US' : LOCALE, {
    hour: isTwelveHours ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12: isTwelveHours,
    ...(timeZone === 'device' ? {} : { timeZone: timeZone ?? getFormatPreferences().timeZone }),
  }).format(date);
}

/**
 * DATE é uma data pura (YYYY-MM-DD) e é formatada sem fuso, como no Twenty.
 * @param {string | null | undefined} value
 */
export function formatDate(value) {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return joinDateParts(calendarPartsOf(date, 'UTC'));
}

/**
 * Só a data (DD/MM/AAAA, ou a ordem da preferência) de um instante, no fuso da preferência do membro: um instante das
 * 21:27 de Brasília é "11/10", não o dia seguinte de UTC, como formatDate (feito para datas puras) daria.
 * @param {string | null | undefined} value
 */
export function formatInstantDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return joinDateParts(calendarPartsOf(date, getFormatPreferences().timeZone));
}

/**
 * Data e hora de um instante no fuso da preferência.
 * @param {string | null | undefined} value
 */
export function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${joinDateParts(calendarPartsOf(date, getFormatPreferences().timeZone))}, ${formatTime(date)}`;
}

/**
 * Data curta de um instante: "7 de out. de 2026" ou, com AAAA-MM-DD, a data numérica.
 * @param {string | null | undefined} value
 */
export function formatShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const { dateFormat, timeZone } = getFormatPreferences();
  if (dateFormat === 'YEAR_FIRST') return joinDateParts(calendarPartsOf(date, timeZone));
  return cachedDateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone }).format(date);
}

/**
 * @param {Date} date
 * @param {boolean} withYear
 */
export function formatMonthLabel(date, withYear) {
  const { timeZone } = getFormatPreferences();
  const formatted = cachedDateTimeFormat(LOCALE, withYear ? { month: 'long', year: 'numeric', timeZone } : { month: 'long', timeZone }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

/**
 * Data pura (YYYY-MM-DD) relativa a hoje, em dias: "hoje", "ontem", "há 3 dias", "em 2 semanas".
 * @param {string | null | undefined} value
 * @param {Date} [now]
 */
export function formatRelativeDate(value, now = new Date()) {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const target = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((target - today) / 86_400_000);
  const absoluteDays = Math.abs(days);
  if (absoluteDays >= 365) return relativeTimeFormatter.format(Math.round(days / 365), 'year');
  if (absoluteDays >= 30) return relativeTimeFormatter.format(Math.round(days / 30), 'month');
  if (absoluteDays >= 7) return relativeTimeFormatter.format(Math.round(days / 7), 'week');
  return relativeTimeFormatter.format(days, 'day');
}

/**
 * @param {string | null | undefined} value
 * @param {Date} [now]
 */
export function formatRelativeToNow(value, now = new Date()) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  /** @type {[Intl.RelativeTimeFormatUnit, number][]} */
  const units = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, unitSeconds] of units) {
    if (absoluteSeconds >= unitSeconds) {
      return relativeTimeFormatter.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return relativeTimeFormatter.format(0, 'second');
}

/**
 * Data de hoje (YYYY-MM-DD) no fuso da preferência.
 * @param {Date} [now]
 */
export function todayIsoDate(now = new Date()) {
  const { year, month, day } = calendarPartsOf(now, getFormatPreferences().timeZone);
  return `${year}-${month}-${day}`;
}

/**
 * Formatação simples de telefone brasileiro; outros países mostram o número cru.
 * @param {{ primaryPhoneNumber?: string | null, primaryPhoneCallingCode?: string | null, primaryPhoneCountryCode?: string | null } | null | undefined} phones
 */
export function formatPhone(phones) {
  if (!phones?.primaryPhoneNumber) return '';
  const callingCode = phones.primaryPhoneCallingCode || '';
  const digits = String(phones.primaryPhoneNumber).replace(/\D/g, '');
  if (callingCode === '+55' && (digits.length === 10 || digits.length === 11)) {
    const areaCode = digits.slice(0, 2);
    const firstPart = digits.slice(2, digits.length - 4);
    const lastPart = digits.slice(-4);
    return `+55 ${areaCode} ${firstPart}-${lastPart}`;
  }
  return `${callingCode} ${phones.primaryPhoneNumber}`.trim();
}

/**
 * @param {string} url
 */
export function getUrlHostname(url) {
  try {
    const absoluteUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(absoluteUrl).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * @param {string} url
 */
export function toAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
